import { getContainerItemName, getContextByTargetAndKey, isContainerInitialized } from '../container/container-utils';
import FormUtils from '../form/form-utils';
import { createC11nEnv } from '../interpreter/c11n-env';
import constants from '../constants';
import { isCoexistenceCase } from '../case/common-helper';
import BroadcastUtils from '../utils/broadcast-utils';
import type { ContainerInfo } from '../container/types';

import EventUtils from './event';

class CoexistenceManager {
  /**
   * This method is used to activate container item on tab switch in ajax container for coexistence.
   * @description This exposes the method activate container item on tab switch in ajax container for coexistence.
   * @example <caption>Example for activateContainerItemOnSwitch()</caption>
   * Example can be seen in pzpega_ui_redux_impl in Infinity
   * PCore.getCoexistenceManager().activateContainerItemOnSwitch(key);
   * @param key caseID of the work item to activate
   * @function
   */
  activateContainerItemOnSwitch(key: string) {
    const target = `${constants.APP.APP}/${constants.PRIMARY}`;
    const containerItemID = getContainerItemName(target, key);
    const containerInfo: ContainerInfo = {
      target,
      containerItemID
    };
    const newC11nEnv = createC11nEnv({});
    newC11nEnv.getPConnect().getContainerManager().activateContainerItem(containerInfo);
  }

  /**
   * This method is used to remove container item on close in ajax container for coexistence.
   * @description This exposes the method remove container item on close in ajax container for coexistence.
   * @example <caption>Example for removeContainerItemOnClose()</caption>
   * Example can be seen in pzpega_ui_redux_impl in Infinity
   * PCore.getCoexistenceManager().removeContainerItemOnClose(key);
   * @param key caseID of the work item for which to remove container item
   * @function
   */
  removeContainerItemOnClose(key: string) {
    const target = `${constants.APP.APP}/${constants.PRIMARY}`;
    const containerItemID = getContainerItemName(target, key);
    const containerInfo = {
      target,
      containerItemID
    };
    const newC11nEnv = createC11nEnv({});
    newC11nEnv.getPConnect().getContainerManager().removeContainerItem(containerInfo);
  }

  /**
   * This method is used to show preview in coexistence for constellation cases.
   * @description This exposes the method to show preview in coexistence for constellation cases.
   * @example <caption>Example for showCasePreview()</caption>
   * Example can be seen in bootstarp-shell.js loadPreview api
   * PCore.getCoexistenceManager().showCasePreview(key, caseClassName);
   * @param key caseID of the work item for which preview needs to be shown
   * @param caseClassName caseClassName of the work item
   * @function
   */
  showCasePreview(key: string, caseClassName: string) {
    const targetPreview = 'root/hybrid_preview';
    const newC11nEnv = createC11nEnv({
      meta: {
        type: 'PreviewViewContainer',
        config: {
          name: constants.PREVIEW
        }
      },
      options: {
        context: constants.APP.APP,
        target: targetPreview
      }
    });
    newC11nEnv
      .getPConnect()
      .getContainerManager()
      .resetContainers({ name: constants.PRIMARY, context: constants.APP.APP });
    if (!isContainerInitialized(constants.APP.APP, constants.PREVIEW)) {
      newC11nEnv
        .getPConnect()
        .getContainerManager()
        .initializeContainers({ type: 'multiple', name: constants.PREVIEW });
    }

    newC11nEnv.getPConnect().getActionsApi().showCasePreview(key, { caseClassName });
  }

  /**
   * This method is used to close the preview in coexistence for constellation cases.
   * @description This exposes the method to close the open preview panel in coexistence for constellation cases.
   * @example <caption>Example for closeCasePreview()</caption>
   * Example can be seen in pz-cosmos-preview-panel in Infinity
   * PCore.getCoexistenceManager().closeCasePreview();
   * @function
   */
  closeCasePreview() {
    const newC11nEnv = createC11nEnv({});
    newC11nEnv
      .getPConnect()
      .getContainerManager()
      .resetContainers({ name: constants.PREVIEW, context: constants.APP.APP });
  }

  /**
   * This method is used to check if a constellation case is dirty in coexistence.
   * @description This exposes the method to check if a constellation case if dirty in coexistence.
   * @example <caption>Example for isFormDirty()</caption>
   * Example can be seen in pzpega_ui_doc_dirtyhandling in Infinity
   * PCore.getCoexistenceManager().isFormDirty(key);
   * @param key caseID of the work item for which to check the form is dirty
   * @returns if form is dirty or not
   * @function
   */
  isFormDirty(key: string) {
    const target = `${constants.APP.APP}/${constants.PRIMARY}`;
    const context = getContextByTargetAndKey(target, key);
    return FormUtils.isStateModified(context);
  }

  /**
   * This method is used to get the EventUtils Object
   * @description This exposes the method to get the EventUtils Object.
   * @example <caption>Example for getEventUtils()</caption>
   * Example can be seen in bootstrap-shell.js
   * PCore.getCoexistenceManager().getEventUtils().publishConstellationLoadedEvent()
   * @returns the EventUtils Object
   * @function
   */
  getEventUtils() {
    return EventUtils;
  }

  /**
   * This method is used to get the BroadcastUtils Object
   * @description This exposes the method to get the BroadcastUtils Object.
   * @example <caption>Example for getBroadcastUtils()</caption>
   * Example can be seen in bootstrap-shell.js
   * PCore.getCoexistenceManager().getBroadcastUtils().isReverseCoexistenceCaseLoaded()
   * @returns the BroadcastUtils Object
   * @function
   */
  getBroadcastUtils(): typeof BroadcastUtils {
    return BroadcastUtils;
  }

  /**
   * this function returns whether given case is traditional ui case(coexistence in constellation) or not
   * @param className - classname of the case
   * @returns - returns boolean if case is traditional ui case or not
   * Example
   * PCore.getCoexistenceManager().isTraditionalUICase('BANK-Hyd-work-Loan')
   */
  isTraditionalUICase(className: string) {
    return isCoexistenceCase(className);
  }
}

export default new CoexistenceManager();
